import { api } from './client'

// ============================================================================
// Lô phân loại — pattern PNK + PXK với items M-N (Phase 3-lots-redesign)
// Status sống trên phiếu: pending|approved|rejected|cancelled
// ============================================================================

export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export type SortingLotInReason =
    | 'from_purchase'
    | 'adjustment'
    | 'return_from_scenting'
    | 'from_sorting_process'
export type SortingLotOutReason = 'for_scenting' | 'discard' | 'adjustment'

export interface SortingLot {
    id: string
    code: string
    grade_id: string
    grade_name?: string
    quantity_in: number
    quantity_available: number
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface SortingLotStockInNoteItem {
    id: string
    note_id: string
    sorting_lot_id: string
    sorting_lot_code?: string
    grade_id?: string | null
    grade_name?: string
    quantity: number
    created_at: string
    /** Nguồn tầng dòng (reason=from_sorting_process): danh sách lô chè mua nguồn + kg. */
    sources?: SortingLotStockInNoteItemSource[]
}

export interface SortingLotStockInNoteItemSource {
    id: string
    note_item_id: string
    source_purchase_lot_id: string
    source_quantity: number
    source_purchase_lot_code?: string
}

export interface SortingLotStockInNote {
    id: string
    code: string
    status: NoteStatus
    reason: SortingLotInReason
    /** PXK lô chè mua nguồn — hệ thống tự điền khi duyệt from_sorting_process. */
    purchase_lot_stock_out_note_id?: string | null
    purchase_lot_stock_out_note_code?: string
    /** Phiếu lấy hàng kho chè mua đã chọn làm gợi ý (ghi lại, cho sửa). */
    purchase_lot_withdrawal_note_id?: string | null
    stocktake_session_id?: string | null
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    created_at: string
    updated_at: string
    items_count?: number
    items?: SortingLotStockInNoteItem[]
}

export type SortingLotStockOutNoteItem = SortingLotStockInNoteItem

export interface SortingLotStockOutNote extends Omit<
    SortingLotStockInNote,
    'reason' | 'items'
> {
    reason: SortingLotOutReason
    items?: SortingLotStockOutNoteItem[]
    /** PXK đã bị một PNK lấy hương sống link (chỉ có ở API detail). */
    has_active_in_note?: boolean
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) — phiếu độc lập, không đụng tồn kho.
// Vòng đời: open|closed|cancelled. Không có bước duyệt.
// ============================================================================

export type WithdrawalNoteStatus = 'open' | 'closed' | 'cancelled'

export interface SortingLotWithdrawalNoteItem {
    id: string
    note_id: string
    sorting_lot_id: string
    sorting_lot_code?: string
    grade_id?: string | null
    grade_name?: string
    quantity: number
    created_at: string
}

export interface SortingLotWithdrawalNote {
    id: string
    code: string
    status: WithdrawalNoteStatus
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
    items_count?: number
    items?: SortingLotWithdrawalNoteItem[]
}

export interface CreateWithdrawalNoteItemInput {
    sorting_lot_id: string
    quantity: number
}

export interface CreateWithdrawalNoteRequest {
    notes?: string
    items: CreateWithdrawalNoteItemInput[]
}

export interface ListWithdrawalNotesParams {
    status?: WithdrawalNoteStatus
    search?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface SortingLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
}

// ============================================================================
// Request shapes
// ============================================================================

/** Một lô nguồn của một dòng phiếu nhập. */
export interface ItemSourceInput {
    source_lot_id: string
    source_quantity: number
}

/** Nguồn tầng dòng — danh sách nhiều lô, chỉ gửi khi reason=from_sorting_process. */
interface ItemSource {
    sources?: ItemSourceInput[]
}

export type CreateInNoteItemInput =
    | ({
          sorting_lot_id: string
          quantity: number
      } & ItemSource)
    | ({
          new_lot_grade_id: string
          new_lot_code?: string
          quantity: number
      } & ItemSource)

export interface CreateInNoteRequest {
    reason: SortingLotInReason
    /** PXK lô chè mua tự sinh khi duyệt — không gửi upfront (giữ cho tương thích). */
    purchase_lot_stock_out_note_id?: string
    /** Phiếu lấy hàng kho chè mua làm gợi ý (được lưu + trừ số dư khi tạo). */
    source_withdrawal_note_id?: string
    notes?: string
    items: CreateInNoteItemInput[]
}

/** Sửa PNK lô phân loại đang pending. Reason bất biến. */
export interface UpdateInNoteRequest {
    reason: SortingLotInReason
    purchase_lot_stock_out_note_id?: string
    source_withdrawal_note_id?: string
    notes?: string
    items: CreateInNoteItemInput[]
}

export interface CreateOutNoteItemInput {
    sorting_lot_id: string
    quantity: number
}

export interface CreateOutNoteRequest {
    reason: SortingLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

/** Sửa PXK lô phân loại đang pending. Reason bất biến; gửi lại toàn bộ item. */
export interface UpdateOutNoteRequest {
    reason: SortingLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

export interface ListLotsParams {
    grade_id?: string
    search?: string
    only_available?: '1' | undefined
    page?: number
    per_page?: number
}

export interface ListNotesParams {
    status?: NoteStatus
    reason?: string
    date_from?: string
    date_to?: string
    /** Lọc PNK theo PXK lô chè mua nguồn. Chỉ có tác dụng với listInNotes. */
    purchase_lot_stock_out_note_id?: string
    page?: number
    per_page?: number
}

// ============================================================================
// API endpoints
// ============================================================================

const BASE = '/sorting-lots'

export const sortingLotApi = {
    // Lots
    listLots: (params?: ListLotsParams) =>
        api.get<SortingLot[]>(BASE, { params }),
    getLot: (id: string) => api.get<SortingLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) => api.get<SortingLotHistoryEntry[]>(`${BASE}/${id}/history`, { params }),

    // Stock-in notes
    listInNotes: (params?: ListNotesParams) =>
        api.get<SortingLotStockInNote[]>(`${BASE}/stock-in-notes`, { params }),
    getInNote: (id: string) =>
        api.get<SortingLotStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreateInNoteRequest) =>
        api.post<SortingLotStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdateInNoteRequest) =>
        api.put<SortingLotStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),

    // Stock-out notes
    listOutNotes: (params?: ListNotesParams) =>
        api.get<SortingLotStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    /**
     * PXK đã duyệt, lý do "để lấy hương" — picker khi tạo PNK lô lấy hương.
     * Không lọc PXK đã dùng (1 PXK sinh bao nhiêu PNK cũng được) nên BE giới hạn
     * 100 dòng; dùng search để thu hẹp.
     */
    listOutNotesForScenting: (search?: string) =>
        api.get<SortingLotStockOutNote[]>(
            `${BASE}/stock-out-notes/for-scenting`,
            { params: search ? { search } : undefined },
        ),
    getOutNote: (id: string) =>
        api.get<SortingLotStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreateOutNoteRequest) =>
        api.post<SortingLotStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdateOutNoteRequest) =>
        api.put<SortingLotStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),

    // Phiếu lấy hàng (withdrawal notes)
    listWithdrawalNotes: (params?: ListWithdrawalNotesParams) =>
        api.get<SortingLotWithdrawalNote[]>(`${BASE}/withdrawal-notes`, {
            params,
        }),
    getWithdrawalNote: (id: string) =>
        api.get<SortingLotWithdrawalNote>(`${BASE}/withdrawal-notes/${id}`),
    createWithdrawalNote: (req: CreateWithdrawalNoteRequest) =>
        api.post<SortingLotWithdrawalNote>(`${BASE}/withdrawal-notes`, req),
    cancelWithdrawalNote: (id: string) =>
        api.post<void>(`${BASE}/withdrawal-notes/${id}/cancel`),
}
